import React, { useState, useEffect, useRef } from 'react';
import QRCodeStyling from 'qr-code-styling';
import { HexColorPicker } from 'react-colorful';
import './QRCodeGenerator.css';

type DotType = 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'square' | 'extra-rounded';
type CornerSquareType = 'dot' | 'square' | 'extra-rounded';
type CornerDotType = 'dot' | 'square';
type ErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H';

interface QRCodeOptions {
  text: string;
  width: number;
  height: number;
  errorCorrectionLevel: ErrorCorrectionLevel;
  dotsColor: string;
  backgroundColor: string;
  dotType: DotType;
  cornerSquareType: CornerSquareType;
  cornerDotType: CornerDotType;
  cornerSquareColor: string;
  cornerDotColor: string;
  logoSize: number;
  logoMargin: number;
}

const QRCodeGenerator: React.FC = () => {
  const [options, setOptions] = useState<QRCodeOptions>({
    text: 'https://example.com',
    width: 300,
    height: 300,
    errorCorrectionLevel: 'M',
    dotsColor: '#000000',
    backgroundColor: '#ffffff',
    dotType: 'square',
    cornerSquareType: 'square',
    cornerDotType: 'square',
    cornerSquareColor: '#000000',
    cornerDotColor: '#000000',
    logoSize: 0.4,
    logoMargin: 5,
  });

  const [logo, setLogo] = useState<string | undefined>(undefined);
  const [activeColorPicker, setActiveColorPicker] = useState<string | null>(null);
  const qrCodeRef = useRef<QRCodeStyling | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!qrCodeRef.current) {
      qrCodeRef.current = new QRCodeStyling({
        width: options.width,
        height: options.height,
        type: 'canvas',
        data: options.text,
        image: logo,
        qrOptions: {
          errorCorrectionLevel: options.errorCorrectionLevel,
        },
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: options.logoSize,
          margin: options.logoMargin,
        },
        dotsOptions: {
          color: options.dotsColor,
          type: options.dotType,
        },
        backgroundOptions: {
          color: options.backgroundColor,
        },
        cornersSquareOptions: {
          color: options.cornerSquareColor,
          type: options.cornerSquareType,
        },
        cornersDotOptions: {
          color: options.cornerDotColor,
          type: options.cornerDotType,
        },
      });

      if (canvasRef.current) {
        canvasRef.current.innerHTML = '';
        qrCodeRef.current.append(canvasRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (qrCodeRef.current) {
      qrCodeRef.current.update({
        width: options.width,
        height: options.height,
        data: options.text,
        image: logo,
        qrOptions: {
          errorCorrectionLevel: options.errorCorrectionLevel,
        },
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: options.logoSize,
          margin: options.logoMargin,
        },
        dotsOptions: {
          color: options.dotsColor,
          type: options.dotType,
        },
        backgroundOptions: {
          color: options.backgroundColor,
        },
        cornersSquareOptions: {
          color: options.cornerSquareColor,
          type: options.cornerSquareType,
        },
        cornersDotOptions: {
          color: options.cornerDotColor,
          type: options.cornerDotType,
        },
      });
    }
  }, [options, logo]);

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogo(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDownload = (extension: 'png' | 'jpeg' | 'svg') => {
    if (qrCodeRef.current) {
      qrCodeRef.current.download({
        name: 'qr-code',
        extension,
      });
    }
  };

  const updateOption = <K extends keyof QRCodeOptions>(
    key: K,
    value: QRCodeOptions[K]
  ) => {
    setOptions((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="qr-generator">
      <div className="controls-panel">
        <h1>QR Code Generator</h1>

        <div className="control-section">
          <h3>Content</h3>
          <label>
            Text/URL:
            <textarea
              value={options.text}
              onChange={(e) => updateOption('text', e.target.value)}
              placeholder="Enter text or URL"
              rows={3}
            />
          </label>
        </div>

        <div className="control-section">
          <h3>Size</h3>
          <label>
            Width: {options.width}px
            <input
              type="range"
              min="200"
              max="600"
              value={options.width}
              onChange={(e) => updateOption('width', Number(e.target.value))}
            />
          </label>
          <label>
            Height: {options.height}px
            <input
              type="range"
              min="200"
              max="600"
              value={options.height}
              onChange={(e) => updateOption('height', Number(e.target.value))}
            />
          </label>
        </div>

        <div className="control-section">
          <h3>Error Correction</h3>
          <select
            value={options.errorCorrectionLevel}
            onChange={(e) =>
              updateOption('errorCorrectionLevel', e.target.value as ErrorCorrectionLevel)
            }
          >
            <option value="L">Low (7%)</option>
            <option value="M">Medium (15%)</option>
            <option value="Q">Quartile (25%)</option>
            <option value="H">High (30%)</option>
          </select>
        </div>

        <div className="control-section">
          <h3>Colors</h3>
          <div className="color-control">
            <label>Dots Color:</label>
            <div className="color-picker-wrapper">
              <div
                className="color-swatch"
                style={{ backgroundColor: options.dotsColor }}
                onClick={() =>
                  setActiveColorPicker(
                    activeColorPicker === 'dots' ? null : 'dots'
                  )
                }
              />
              <span>{options.dotsColor}</span>
            </div>
            {activeColorPicker === 'dots' && (
              <HexColorPicker
                color={options.dotsColor}
                onChange={(color) => updateOption('dotsColor', color)}
              />
            )}
          </div>

          <div className="color-control">
            <label>Background Color:</label>
            <div className="color-picker-wrapper">
              <div
                className="color-swatch"
                style={{ backgroundColor: options.backgroundColor }}
                onClick={() =>
                  setActiveColorPicker(
                    activeColorPicker === 'background' ? null : 'background'
                  )
                }
              />
              <span>{options.backgroundColor}</span>
            </div>
            {activeColorPicker === 'background' && (
              <HexColorPicker
                color={options.backgroundColor}
                onChange={(color) => updateOption('backgroundColor', color)}
              />
            )}
          </div>

          <div className="color-control">
            <label>Corner Square Color:</label>
            <div className="color-picker-wrapper">
              <div
                className="color-swatch"
                style={{ backgroundColor: options.cornerSquareColor }}
                onClick={() =>
                  setActiveColorPicker(
                    activeColorPicker === 'cornerSquare' ? null : 'cornerSquare'
                  )
                }
              />
              <span>{options.cornerSquareColor}</span>
            </div>
            {activeColorPicker === 'cornerSquare' && (
              <HexColorPicker
                color={options.cornerSquareColor}
                onChange={(color) => updateOption('cornerSquareColor', color)}
              />
            )}
          </div>

          <div className="color-control">
            <label>Corner Dot Color:</label>
            <div className="color-picker-wrapper">
              <div
                className="color-swatch"
                style={{ backgroundColor: options.cornerDotColor }}
                onClick={() =>
                  setActiveColorPicker(
                    activeColorPicker === 'cornerDot' ? null : 'cornerDot'
                  )
                }
              />
              <span>{options.cornerDotColor}</span>
            </div>
            {activeColorPicker === 'cornerDot' && (
              <HexColorPicker
                color={options.cornerDotColor}
                onChange={(color) => updateOption('cornerDotColor', color)}
              />
            )}
          </div>
        </div>

        <div className="control-section">
          <h3>Dot Style</h3>
          <select
            value={options.dotType}
            onChange={(e) => updateOption('dotType', e.target.value as DotType)}
          >
            <option value="square">Square</option>
            <option value="dots">Dots</option>
            <option value="rounded">Rounded</option>
            <option value="extra-rounded">Extra Rounded</option>
            <option value="classy">Classy</option>
            <option value="classy-rounded">Classy Rounded</option>
          </select>
        </div>

        <div className="control-section">
          <h3>Corner Square Style</h3>
          <select
            value={options.cornerSquareType}
            onChange={(e) =>
              updateOption('cornerSquareType', e.target.value as CornerSquareType)
            }
          >
            <option value="square">Square</option>
            <option value="dot">Dot</option>
            <option value="extra-rounded">Extra Rounded</option>
          </select>
        </div>

        <div className="control-section">
          <h3>Corner Dot Style</h3>
          <select
            value={options.cornerDotType}
            onChange={(e) =>
              updateOption('cornerDotType', e.target.value as CornerDotType)
            }
          >
            <option value="square">Square</option>
            <option value="dot">Dot</option>
          </select>
        </div>

        <div className="control-section">
          <h3>Logo</h3>
          <label className="file-upload">
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
            />
            Upload Logo
          </label>
          {logo && (
            <>
              <div className="logo-preview">
                <img src={logo} alt="Logo preview" />
              </div>
              <button onClick={() => setLogo(undefined)} className="remove-logo">
                Remove Logo
              </button>
              <label>
                Logo Size: {Math.round(options.logoSize * 100)}%
                <input
                  type="range"
                  min="0.1"
                  max="0.6"
                  step="0.05"
                  value={options.logoSize}
                  onChange={(e) => updateOption('logoSize', Number(e.target.value))}
                />
              </label>
              <label>
                Logo Margin: {options.logoMargin}px
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={options.logoMargin}
                  onChange={(e) => updateOption('logoMargin', Number(e.target.value))}
                />
              </label>
            </>
          )}
        </div>

        <div className="control-section download-section">
          <h3>Download</h3>
          <div className="download-buttons">
            <button onClick={() => handleDownload('png')}>PNG</button>
            <button onClick={() => handleDownload('jpeg')}>JPEG</button>
            <button onClick={() => handleDownload('svg')}>SVG</button>
          </div>
        </div>
      </div>

      <div className="preview-panel">
        <h2>Preview</h2>
        <div className="qr-code-container">
          <div ref={canvasRef} />
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;
